import { useState, useImperativeHandle, forwardRef } from "react";
import { useAtomValue } from "jotai";
import clsx from "clsx";
import { fileProgresses } from "@/frontend/atoms/data-room/upload";
import { Typography } from "@/frontend/shared/ui/Typography";
import { CaretDownIcon, CheckIcon } from "@/frontend/shared/assets/icons";
import styles from "./DataroomfileProgressModal.module.scss";

export type TDataroomModalHandles = {
  openModal: () => void;
};

export const DataroomFileProgressModal = forwardRef<TDataroomModalHandles>((_props, ref) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const _fileProgresses = useAtomValue(fileProgresses);
  const progresses = Object.entries(_fileProgresses);

  useImperativeHandle(ref, () => {
    return {
      openModal: () => setIsExpanded(true)
    };
  });

  if (!progresses?.length) {
    return null;
  }

  return (
    <div className={clsx(styles.container, isExpanded && progresses.length > 0 && styles.containerExpanded)}>
      <div className={styles.header}>
        <Typography variant="heading-7" color="greyscale800">
          File Upload
        </Typography>
        <div className={styles.headerRightSide}>
          <CaretDownIcon
            className={clsx(
              styles.headerCaretDownIcon,
              isExpanded && progresses.length > 0 && styles.headerCaretDownIconExpanded
            )}
            onClick={() => setIsExpanded((prev) => !prev)}
          />
        </div>
      </div>

      <div className={styles.content}>
        {progresses.map(([filename, { progress, status, message }]) => {
          const key = `fileUpload-${filename}`;
          return (
            <div className={styles.row} key={key}>
              <div className={styles.companyInfo}>
                <div className={styles.companyShortInfo}>
                  <Typography variant="label-4" color="greyscale800">
                    {filename}
                  </Typography>
                  {status === "errored" ? (
                    <Typography variant="body-3" color="error-red">
                      {message ?? "Error uploading file. Please upload a new file or try again."}
                    </Typography>
                  ) : (
                    <Typography variant="body-3" color="greyscale600">
                      {message ?? (progress >= 100 ? "File uploaded" : "Uploading")}
                    </Typography>
                  )}
                </div>
                {status === "completed" ? (
                  <div className={styles.checkIconContainer}>
                    <CheckIcon className={styles.checkIcon} />
                  </div>
                ) : null}
              </div>
              {status === "errored" ? (
                <div className={styles.progressContainer}>
                  <div className={clsx(styles.progress, styles.errorProgress)} />
                </div>
              ) : (
                <div className={styles.progressContainer}>
                  <div className={styles.progress} style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
